import {Axis} from "../../axis/axis";
import type {CrosspointDataServiceFactoryConfig} from "../types/crosspoint-data-service-factory.type";
import type {CrosspointDataService} from "../types/crosspoint-data-service.interface";

/**
 * A factory class responsible for creating instances of `CrosspointDataService`.
 *
 * Each call to create() returns a NEW instance since each DataProvider
 * requires its own service instance with specific axis configurations.
 *
 * @template TXAxis - The type for X-axis values
 * @template TYAxis - The type for Y-axis values
 * @template TItem - The type of items managed by the service
 */
export class CrosspointDataServiceFactory {

    /**
     * Creates a new instance of `CrosspointDataService` based on the provided configuration and axes.
     *
     * IMPORTANT: Each call creates a NEW instance. This is intentional because:
     * - Different DataProviders may use the same service class but different axes
     * - Each DataProvider needs its own service instance with correct axis references
     *
     * @param {CrosspointDataServiceFactoryConfig<TXAxis, TYAxis, TItem>} config - The configuration object for creating the `CrosspointDataService` instance.
     * @param {Axis<TXAxis>} xAxis - The matrix axis representing the horizontal (X-axis) configuration.
     * @param {Axis<TYAxis>} yAxis - The matrix axis representing the vertical (Y-axis) configuration.
     * @return {CrosspointDataService<TItem>} A new instance of `CrosspointDataService`.
     */
    static create<TXAxis, TYAxis, TItem>(
        config: CrosspointDataServiceFactoryConfig<TXAxis, TYAxis, TItem>,
        xAxis: Axis<TXAxis>,
        yAxis: Axis<TYAxis>
    ): CrosspointDataService<TItem> {
        return new config.class(config, xAxis, yAxis);
    }

}